/**
 * TOTP (Time-based One-Time Password) 계산 유틸리티
 * RFC 6238 구현
 *
 * crypto-js 라이브러리 사용 (HTTPS 불필요)
 * crypto.subtle API는 Secure Context (HTTPS)에서만 동작하므로
 * 개발시 http://192.168.0.XXX:3000 사용을 위해 crypto-js로 변경함.
 */

import CryptoJS from 'crypto-js';

// Base32 디코딩
function base32Decode(encoded) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let bits = '';
  let result = [];

  // 패딩 제거 및 대문자 변환
  encoded = encoded.replace(/=+$/, '').toUpperCase();

  for (let i = 0; i < encoded.length; i++) {
    const val = alphabet.indexOf(encoded[i]);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, '0');
  }

  for (let i = 0; i + 8 <= bits.length; i += 8) {
    result.push(parseInt(bits.substr(i, 8), 2));
  }

  return new Uint8Array(result);
}

// Uint8Array를 CryptoJS WordArray로 변환
function uint8ArrayToWordArray(uint8Array) {
  const words = [];
  for (let i = 0; i < uint8Array.length; i += 4) {
    words.push(
      ((uint8Array[i] || 0) << 24) |
      ((uint8Array[i + 1] || 0) << 16) |
      ((uint8Array[i + 2] || 0) << 8) |
      (uint8Array[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, uint8Array.length);
}

// CryptoJS WordArray를 Uint8Array로 변환
function wordArrayToUint8Array(wordArray) {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const uint8Array = new Uint8Array(sigBytes);

  for (let i = 0; i < sigBytes; i++) {
    uint8Array[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }

  return uint8Array;
}

// HMAC-SHA1 계산 (crypto-js 사용)
function hmacSha1(key, message) {
  const keyWordArray = uint8ArrayToWordArray(key);
  const msgWordArray = uint8ArrayToWordArray(message);
  const hash = CryptoJS.HmacSHA1(msgWordArray, keyWordArray);
  return wordArrayToUint8Array(hash);
}

// HMAC-SHA256 계산 (crypto-js 사용)
function hmacSha256(key, message) {
  const keyWordArray = uint8ArrayToWordArray(key);
  const msgWordArray = uint8ArrayToWordArray(message);
  const hash = CryptoJS.HmacSHA256(msgWordArray, keyWordArray);
  return wordArrayToUint8Array(hash);
}

// HMAC-SHA512 계산 (crypto-js 사용)
function hmacSha512(key, message) {
  const keyWordArray = uint8ArrayToWordArray(key);
  const msgWordArray = uint8ArrayToWordArray(message);
  const hash = CryptoJS.HmacSHA512(msgWordArray, keyWordArray);
  return wordArrayToUint8Array(hash);
}

// 알고리즘에 따른 HMAC 함수 선택
function hmac(algorithm, key, message) {
  switch (algorithm.toUpperCase()) {
    case 'SHA256':
    case 'SHA-256':
      return hmacSha256(key, message);
    case 'SHA512':
    case 'SHA-512':
      return hmacSha512(key, message);
    case 'SHA1':
    case 'SHA-1':
    default:
      return hmacSha1(key, message);
  }
}

// 64비트 정수를 바이트 배열로 변환
function intToBytes(num) {
  const bytes = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    bytes[i] = num & 0xff;
    num = Math.floor(num / 256);
  }
  return bytes;
}

/**
 * TOTP 코드 생성
 * @param {string} secret - Base32 인코딩된 시크릿 키
 * @param {string} algorithm - 해시 알고리즘 (SHA1, SHA256, SHA512)
 * @param {number} digits - OTP 자릿수 (기본값 6)
 * @param {number} period - 갱신 주기 (초, 기본값 30)
 * @returns {string} - TOTP 코드
 */
export function generateTOTP(secret, algorithm = 'SHA1', digits = 6, period = 30) {
  // 현재 시간 기반 카운터 계산
  const counter = Math.floor(Date.now() / 1000 / period);

  // 시크릿 디코딩
  const key = base32Decode(secret);

  // 카운터를 바이트 배열로 변환
  const message = intToBytes(counter);

  // HMAC 계산 (동기 함수로 변경됨)
  const hash = hmac(algorithm, key, message);

  // Dynamic Truncation
  const offset = hash[hash.length - 1] & 0x0f;
  const binary =
    ((hash[offset] & 0x7f) << 24) |
    ((hash[offset + 1] & 0xff) << 16) |
    ((hash[offset + 2] & 0xff) << 8) |
    (hash[offset + 3] & 0xff);

  // 지정된 자릿수로 변환
  const otp = binary % Math.pow(10, digits);

  // 앞에 0 채우기
  return otp.toString().padStart(digits, '0');
}

/**
 * 현재 주기의 남은 시간 계산
 * @param {number} period - 갱신 주기 (초)
 * @returns {number} - 남은 초
 */
export function getRemainingSeconds(period = 30) {
  return period - (Math.floor(Date.now() / 1000) % period);
}

/**
 * 진행률 계산 (0~100)
 * @param {number} period - 갱신 주기 (초)
 * @returns {number} - 진행률 (%)
 */
export function getProgress(period = 30) {
  const remaining = getRemainingSeconds(period);
  return (remaining / period) * 100;
}
